import React, { useState, useEffect } from 'react';
import { documentosAPI } from '../services/api';
import PlantillaForm from './PlantillaForm';
import './Plantillas.css';

function Plantillas() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadPlantillas();
  }, []);

  const loadPlantillas = async () => {
    try {
      setLoading(true);
      const response = await documentosAPI.getPlantillas(false); // Cargar todas, incluyendo inactivas
      setPlantillas(response.data);
      setError(null);
    } catch (err) {
      setError('Error al cargar las plantillas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlantilla(null);
    setShowForm(true);
  };

  const handleEdit = (plantilla) => {
    setEditingPlantilla(plantilla);
    setShowForm(true);
  };

  const handleDelete = async (id, activa) => {
    if (activa) {
      if (!window.confirm('¿Estás seguro de que quieres desactivar esta plantilla?')) {
        return;
      }
      try {
        await documentosAPI.deletePlantilla(id, false);
        setSuccess('Plantilla desactivada correctamente');
        loadPlantillas();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Error al desactivar la plantilla');
        console.error(err);
      }
    } else {
      if (!window.confirm('ATENCIÓN: Esto eliminará definitivamente la plantilla y no se podrá recuperar. ¿Seguro que quieres continuar?')) {
        return;
      }
      try {
        await documentosAPI.deletePlantilla(id, true);
        setSuccess('Plantilla eliminada definitivamente');
        loadPlantillas();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Error al eliminar definitivamente la plantilla');
        console.error(err);
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingPlantilla(null);
  };

  const handleFormSubmit = async (data, file = null) => {
    try {
      if (editingPlantilla) {
        await documentosAPI.updatePlantilla(editingPlantilla.id, data, file);
        setSuccess('Plantilla actualizada correctamente');
      } else {
        await documentosAPI.createPlantilla(data, file);
        setSuccess('Plantilla creada correctamente');
      }
      setShowForm(false);
      setEditingPlantilla(null);
      loadPlantillas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar la plantilla');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="loading">Cargando plantillas...</div>;
  }

  return (
    <div className="plantillas">
      <div className="page-header">
        <h2>Gestión de Plantillas</h2>
        <button className="btn btn-primary" onClick={handleCreate}>
          Crear Plantilla
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="info-box">
        <h4>Información sobre Plantillas</h4>
        <p>Las plantillas son archivos Word (.docx) que contienen campos personalizables.</p>
        <p>Para crear una plantilla:</p>
        <ul>
          <li>Sube un archivo Word (.docx) como plantilla</li>
          <li>Define los campos personalizables que se pueden rellenar (ej: nombre, email, fecha)</li>
          <li>En el archivo Word, usa el formato {'{{campo}}'} para los campos que quieres reemplazar</li>
        </ul>
        <p><strong>Ejemplo:</strong> Si defines el campo "nombre", en tu Word puedes usar {'{{nombre}}'} y se reemplazará con el valor del cliente.</p>
      </div>

      <div className="plantillas-list">
        {plantillas.map(plantilla => (
          <div key={plantilla.id} className={`plantilla-card ${!plantilla.activa ? 'inactiva' : ''}`}>
            <div className="plantilla-header">
              <div>
                <h3>{plantilla.nombre}</h3>
                {plantilla.descripcion && (
                  <p className="plantilla-descripcion">{plantilla.descripcion}</p>
                )}
                <p className="plantilla-info">
                  Archivo: {plantilla.ruta_archivo.includes('templates/') ? plantilla.ruta_archivo : `templates/${plantilla.ruta_archivo}`}
                </p>
                <p className="plantilla-estado">
                  Estado: {plantilla.activa ? '✅ Activa' : '❌ Inactiva'}
                </p>
                {plantilla.campos_personalizables && plantilla.campos_personalizables.length > 0 && (
                  <div className="plantilla-campos">
                    <strong>Campos personalizables:</strong>
                    <ul>
                      {plantilla.campos_personalizables.map((campo, index) => (
                        <li key={index}>{campo}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="plantilla-actions">
              <button
                className="btn btn-edit btn-sm"
                onClick={() => handleEdit(plantilla)}
              >
                Editar
              </button>
              <a
                className="btn btn-secondary btn-sm"
                href={`/api/plantillas_download.php?id=${plantilla.id}`}
                download
              >
                Descargar
              </a>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleDelete(plantilla.id, plantilla.activa)}
              >
                {plantilla.activa ? 'Desactivar' : 'Eliminar'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {plantillas.length === 0 && (
        <div className="empty-state">
          <p>No hay plantillas creadas. Crea la primera haciendo clic en "Crear Plantilla".</p>
        </div>
      )}

      {showForm && (
        <PlantillaForm
          plantilla={editingPlantilla}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}

export default Plantillas;

